import React, { useEffect, useState } from "react"
import {
  Box,
  Flex,
  Spinner,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react"
import { getMonthlyReportPivotSlaCompliance } from "../../../Http/Index"

interface Props {
  selectedMonth: string
  selectedCompanyId?: string
}

interface SlaSection {
  total: number
  sla: number
  outOfSla: number
  slaPercent: number
  outOfSlaPercent: number
}

interface SlaData {
  firstResponse?: SlaSection
  drConsult?: SlaSection
  ambDispatch?: SlaSection
  hospArrival?: SlaSection
}

export const SlaCompliance: React.FC<Props> = ({
  selectedMonth,
  selectedCompanyId = "",
}) => {
  const [data, setData] = useState<SlaData | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (selectedMonth) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedCompanyId])

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const params: Record<string, any> = {}
      if (selectedCompanyId && selectedCompanyId.trim()) params.companyId = selectedCompanyId.trim()
      const res = await getMonthlyReportPivotSlaCompliance(selectedMonth, params)
      const resBody = res?.data
      const slaPayload = (resBody?.data && resBody.data.firstResponse)
        ? resBody.data
        : (resBody?.firstResponse ? resBody : null)
      setData(slaPayload)
    } catch {
      setData(null)
    } finally {
      setIsLoading(false)
    }
  }

  const fr = data?.firstResponse ?? { total: 0, sla: 0, outOfSla: 0, slaPercent: 0, outOfSlaPercent: 0 }
  const dc = data?.drConsult ?? { total: 0, sla: 0, outOfSla: 0, slaPercent: 0, outOfSlaPercent: 0 }
  const ad = data?.ambDispatch ?? { total: 0, sla: 0, outOfSla: 0, slaPercent: 0, outOfSlaPercent: 0 }
  const ha = data?.hospArrival ?? { total: 0, sla: 0, outOfSla: 0, slaPercent: 0, outOfSlaPercent: 0 }

  return (
    <Box mt={2}>
      {isLoading && (
        <Flex justify="center" align="center" py={4}>
          <Spinner size="md" color="blue.500" mr={3} />
          <Text color="gray.500" fontSize="sm">Loading SLA Compliance Data...</Text>
        </Flex>
      )}

      {/* Summary Stat Cards - Always Visible */}
      <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} mb={6}>
        <Box p={4} borderWidth={1} borderRadius="lg" bg="green.50" borderColor="green.200" boxShadow="sm">
          <Stat>
            <StatLabel color="green.800" fontWeight="bold">First Response SLA</StatLabel>
            <StatNumber color="green.600">{fr.slaPercent}%</StatNumber>
            <StatHelpText color="green.700" mb={0}>
              {fr.sla} / {fr.total} cases within ≤2 min
            </StatHelpText>
          </Stat>
        </Box>

        <Box p={4} borderWidth={1} borderRadius="lg" bg="teal.50" borderColor="teal.200" boxShadow="sm">
          <Stat>
            <StatLabel color="teal.800" fontWeight="bold">Dr Consult SLA</StatLabel>
            <StatNumber color="teal.600">{dc.slaPercent}%</StatNumber>
            <StatHelpText color="teal.700" mb={0}>
              {dc.sla} / {dc.total} cases within ≤5 min
            </StatHelpText>
          </Stat>
        </Box>

        <Box p={4} borderWidth={1} borderRadius="lg" bg="blue.50" borderColor="blue.200" boxShadow="sm">
          <Stat>
            <StatLabel color="blue.800" fontWeight="bold">Ambulance Dispatch SLA</StatLabel>
            <StatNumber color="blue.600">{ad.slaPercent}%</StatNumber>
            <StatHelpText color="blue.700" mb={0}>
              {ad.sla} / {ad.total} cases within ≤18 min
            </StatHelpText>
          </Stat>
        </Box>

        <Box p={4} borderWidth={1} borderRadius="lg" bg="purple.50" borderColor="purple.200" boxShadow="sm">
          <Stat>
            <StatLabel color="purple.800" fontWeight="bold">Hospital Arrival SLA</StatLabel>
            <StatNumber color="purple.600">{ha.slaPercent}%</StatNumber>
            <StatHelpText color="purple.700" mb={0}>
              {ha.sla} / {ha.total} cases within ≤55 min
            </StatHelpText>
          </Stat>
        </Box>
      </SimpleGrid>

      {/* Detailed SLA Compliance Table - Always Visible */}
      <Box overflowX="auto" borderWidth={1} borderRadius="lg" boxShadow="sm">
        <Table size="md" variant="simple">
          <Thead bg="gray.100">
            <Tr>
              <Th>SLA Category</Th>
              <Th>Threshold</Th>
              <Th isNumeric>Total Cases</Th>
              <Th isNumeric bg="green.50">In SLA (Count)</Th>
              <Th isNumeric bg="green.50">In SLA (%)</Th>
              <Th isNumeric bg="red.50">Out of SLA (Count)</Th>
              <Th isNumeric bg="red.50">Out of SLA (%)</Th>
            </Tr>
          </Thead>
          <Tbody>
            {/* First Response Row */}
            <Tr _hover={{ bg: "gray.50" }}>
              <Td fontWeight="semibold">First Response (PHA Callback)</Td>
              <Td><Badge colorScheme="green" px={2} py={0.5} borderRadius="md">≤ 2 mins</Badge></Td>
              <Td isNumeric fontWeight="bold">{fr.total}</Td>
              <Td isNumeric bg="green.50" fontWeight="bold" color="green.700">{fr.sla}</Td>
              <Td isNumeric bg="green.50">
                <Badge colorScheme={fr.slaPercent >= 90 ? "green" : fr.slaPercent > 0 ? "orange" : "gray"} fontSize="sm" px={2.5} py={0.5}>
                  {fr.slaPercent}%
                </Badge>
              </Td>
              <Td isNumeric bg="red.50" fontWeight="bold" color="red.700">{fr.outOfSla}</Td>
              <Td isNumeric bg="red.50">
                <Badge colorScheme={fr.outOfSlaPercent > 10 ? "red" : "gray"} fontSize="sm" px={2.5} py={0.5}>
                  {fr.outOfSlaPercent}%
                </Badge>
              </Td>
            </Tr>

            {/* Doctor Consult Row */}
            <Tr _hover={{ bg: "gray.50" }}>
              <Td fontWeight="semibold">Doctor Consultation</Td>
              <Td><Badge colorScheme="teal" px={2} py={0.5} borderRadius="md">≤ 5 mins</Badge></Td>
              <Td isNumeric fontWeight="bold">{dc.total}</Td>
              <Td isNumeric bg="green.50" fontWeight="bold" color="green.700">{dc.sla}</Td>
              <Td isNumeric bg="green.50">
                <Badge colorScheme={dc.slaPercent >= 90 ? "green" : dc.slaPercent > 0 ? "orange" : "gray"} fontSize="sm" px={2.5} py={0.5}>
                  {dc.slaPercent}%
                </Badge>
              </Td>
              <Td isNumeric bg="red.50" fontWeight="bold" color="red.700">{dc.outOfSla}</Td>
              <Td isNumeric bg="red.50">
                <Badge colorScheme={dc.outOfSlaPercent > 10 ? "red" : "gray"} fontSize="sm" px={2.5} py={0.5}>
                  {dc.outOfSlaPercent}%
                </Badge>
              </Td>
            </Tr>

            {/* Ambulance Dispatch Row */}
            <Tr _hover={{ bg: "gray.50" }}>
              <Td fontWeight="semibold">Ambulance Dispatch</Td>
              <Td><Badge colorScheme="blue" px={2} py={0.5} borderRadius="md">≤ 18 mins</Badge></Td>
              <Td isNumeric fontWeight="bold">{ad.total}</Td>
              <Td isNumeric bg="green.50" fontWeight="bold" color="green.700">{ad.sla}</Td>
              <Td isNumeric bg="green.50">
                <Badge colorScheme={ad.slaPercent >= 90 ? "green" : ad.slaPercent > 0 ? "orange" : "gray"} fontSize="sm" px={2.5} py={0.5}>
                  {ad.slaPercent}%
                </Badge>
              </Td>
              <Td isNumeric bg="red.50" fontWeight="bold" color="red.700">{ad.outOfSla}</Td>
              <Td isNumeric bg="red.50">
                <Badge colorScheme={ad.outOfSlaPercent > 10 ? "red" : "gray"} fontSize="sm" px={2.5} py={0.5}>
                  {ad.outOfSlaPercent}%
                </Badge>
              </Td>
            </Tr>

            {/* Hospital Arrival Row */}
            <Tr _hover={{ bg: "gray.50" }}>
              <Td fontWeight="semibold">Hospital Arrival</Td>
              <Td><Badge colorScheme="purple" px={2} py={0.5} borderRadius="md">≤ 55 mins</Badge></Td>
              <Td isNumeric fontWeight="bold">{ha.total}</Td>
              <Td isNumeric bg="green.50" fontWeight="bold" color="green.700">{ha.sla}</Td>
              <Td isNumeric bg="green.50">
                <Badge colorScheme={ha.slaPercent >= 90 ? "green" : ha.slaPercent > 0 ? "orange" : "gray"} fontSize="sm" px={2.5} py={0.5}>
                  {ha.slaPercent}%
                </Badge>
              </Td>
              <Td isNumeric bg="red.50" fontWeight="bold" color="red.700">{ha.outOfSla}</Td>
              <Td isNumeric bg="red.50">
                <Badge colorScheme={ha.outOfSlaPercent > 10 ? "red" : "gray"} fontSize="sm" px={2.5} py={0.5}>
                  {ha.outOfSlaPercent}%
                </Badge>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Box>
  )
}
